
import * as React from 'react';
import { Modal } from './common/Modal';
import { Button } from './common/Button';
import { CheckCircleIcon, DocumentTextIcon, TagIcon, UserIcon } from './common/Icons';

type UserType = 'Brand' | 'Freelancer' | 'Agency';
type FeedbackCategory = 'Feature Request' | 'Bug Report' | 'Question';

export const Feedback: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [userType, setUserType] = React.useState<UserType>('Brand');
  const [category, setCategory] = React.useState<FeedbackCategory>('Feature Request');
  const [feedback, setFeedback] = React.useState('');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const resetForm = () => {
    setUserType('Brand');
    setCategory('Feature Request');
    setFeedback('');
    setName('');
    setEmail('');
    setSubmitted(false);
  };

  const handleOpen = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    // Simulate API call - TODO: Implement actual feedback submission
    setTimeout(() => {
      // Feedback data would be sent to backend here
      void { userType, category, feedback, name, email };
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 2500);
    }, 1000);
  };

  const UserTypeButton: React.FC<{ value: UserType }> = ({ value }) => {
    const isActive = userType === value;
    return (
      <button
        type="button"
        onClick={() => setUserType(value)}
        className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
          isActive
            ? 'bg-cyan-500 text-white shadow'
            : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'
        }`}
      >
        {value}
      </button>
    );
  };

  return (
    <>
      <button onClick={handleOpen} className="hover:text-white transition-colors">
        Share Feedback
      </button>
      <Modal isOpen={isModalOpen} onClose={handleClose} title="Share Your Feedback">
        {submitted ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white">Thank You!</h3>
            <p className="text-slate-400 mt-1">Your feedback has been received.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Who are you? */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-3">
                <UserIcon className="text-slate-400" />
                Who are you?
              </label>
              <div className="flex space-x-2">
                <UserTypeButton value="Brand" />
                <UserTypeButton value="Freelancer" />
                <UserTypeButton value="Agency" />
              </div>
            </div>

            {/* What's this about? */}
            <div>
              <label htmlFor="feedback-category" className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                <TagIcon className="text-slate-400" />
                What's this about?
              </label>
              <select
                id="feedback-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                className="block w-full px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 sm:text-sm"
              >
                <option className="bg-[#1a1a24] text-white">Feature Request</option>
                <option className="bg-[#1a1a24] text-white">Bug Report</option>
                <option className="bg-[#1a1a24] text-white">Question</option>
              </select>
            </div>

            {/* Your feedback */}
            <div>
              <label htmlFor="feedback-text" className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                 <DocumentTextIcon className="text-slate-400" />
                 Your feedback
              </label>
              <textarea
                id="feedback-text"
                rows={4}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell us what you think..."
                required
                className="block w-full px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 sm:text-sm"
              />
            </div>
            
            {/* Name and Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 <div>
                    <label htmlFor="feedback-name" className="block text-sm font-medium text-slate-300 mb-1">Name</label>
                    <input
                      id="feedback-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Alex Doe"
                      className="block w-full px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 sm:text-sm"
                    />
                 </div>
                 <div>
                    <label htmlFor="feedback-email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                    <input
                      id="feedback-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="alex.doe@example.com"
                      className="block w-full px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 sm:text-sm"
                    />
                 </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end items-center space-x-3 pt-4 border-t border-white/10">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !feedback.trim()}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};